'use client'

/**
 * Notification Stream Hook (Issue #1531, #2135, #2663)
 *
 * Provides real-time notification count updates via Server-Sent Events.
 * Falls back to polling if SSE connection fails.
 *
 * Features:
 * - Automatic SSE connection management
 * - Graceful fallback to 60-second polling
 * - Reconnection on visibility change (tab focus)
 * - Proper cleanup on unmount
 * - Auto-reconnect when user ID changes (Issue #2135 - demo mode switching)
 * - Custom event listener for manual refresh triggers (Issue #2663)
 * - Poll backoff on repeated network failure (Issue #16518)
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import type { getUnreadNotificationCount } from '@/app/actions/notifications'
import { reportClientError } from '@/lib/client-error'
import { isExpectedClientFailure } from '@/lib/client-error-classification'
import { ERROR_IDS } from '@/lib/errors/constants'
import type { NotificationEvent } from '@/lib/notification-events'
import { SHELL_ENDPOINTS, shellReadResult } from '@/lib/shell-transport'

/** The action's own envelope, forwarded verbatim by the shell route. */
type UnreadCountResult = Awaited<ReturnType<typeof getUnreadNotificationCount>>

// ============================================================================
// Types
// ============================================================================

interface UseNotificationStreamOptions {
  /**
   * Current user ID. When this changes, the hook will automatically
   * disconnect and reconnect to get notifications for the new user.
   * (Issue #2135 - fixes demo mode user switching)
   */
  userId?: string | null
}

interface UseNotificationStreamResult {
  unreadCount: number
  isConnected: boolean
  error: Error | null
  /** Manually refresh the count (useful after popover actions) */
  refresh: () => Promise<void>
}

// ============================================================================
// Constants
// ============================================================================

const SSE_ENDPOINT = '/api/sse/notifications'
export const FALLBACK_POLL_INTERVAL = 60000 // 60 seconds
export const RECONNECT_DELAY = 5000 // 5 seconds

/**
 * Longest the poll waits after consecutive network failures (#16518). The
 * wait starts at one {@link FALLBACK_POLL_INTERVAL} and doubles per failure.
 */
export const MAX_POLL_BACKOFF = 15 * 60_000 // 15 minutes

/** Wait before the next automatic read after `failures` in a row. */
function pollBackoffDelay(failures: number): number {
  if (failures <= 0) return 0
  return Math.min(
    FALLBACK_POLL_INTERVAL * 2 ** (failures - 1),
    MAX_POLL_BACKOFF,
  )
}

/**
 * Custom event name for triggering notification count refresh.
 * Dispatched by server actions when notifications are modified.
 * (Issue #2663)
 */
export const NOTIFICATIONS_UPDATED_EVENT = 'cadence:notifications-updated'

// ============================================================================
// Hook Implementation
// ============================================================================

export function useNotificationStream(
  options: UseNotificationStreamOptions = {},
): UseNotificationStreamResult {
  const { userId } = options
  const [unreadCount, setUnreadCount] = useState(0)
  const [isConnected, setIsConnected] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  // Refs to track state across effect cleanups
  const eventSourceRef = useRef<EventSource | null>(null)
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const isMountedRef = useRef(true)
  // Track the user ID that the current connection was established for (Issue #2135)
  const connectedUserIdRef = useRef<string | null | undefined>(undefined)
  // Poll backoff (#16518): consecutive network/session/rollout failures, and
  // the earliest time an automatic read may go out again.
  const consecutiveFailuresRef = useRef(0)
  const nextPollAtRef = useRef(0)
  // An automatic read still pending: a hung request has not failed yet, so
  // the backoff has not engaged, and SSE reconnects must not stack more.
  const pollInFlightRef = useRef(false)

  const recordFailure = useCallback((message: unknown) => {
    if (!isExpectedClientFailure(message)) return
    consecutiveFailuresRef.current += 1
    nextPollAtRef.current =
      Date.now() + pollBackoffDelay(consecutiveFailuresRef.current)
  }, [])

  // Manual refresh function. Over HTTP, not the server action: the bell is
  // shell chrome, and a shell action enters the App Router's action queue
  // against the user's navigations (#13434, `src/lib/shell-transport.ts`).
  const refresh = useCallback(async () => {
    try {
      const read = await shellReadResult<UnreadCountResult>(
        SHELL_ENDPOINTS.unreadNotificationCount,
      )
      if (!read.ok) {
        recordFailure(read.error)
        reportClientError({
          errorId: ERROR_IDS.NOTIFICATION_STREAM_REFRESH_FAILED,
          action: 'getUnreadNotificationCount',
          error: new Error(read.error),
        })
        return
      }
      const result = read.data
      consecutiveFailuresRef.current = 0
      nextPollAtRef.current = 0
      if (!result.success) {
        reportClientError({
          errorId: ERROR_IDS.NOTIFICATION_STREAM_REFRESH_FAILED,
          action: 'getUnreadNotificationCount',
          error: new Error(result.error),
        })
        return
      }
      if (isMountedRef.current) {
        setUnreadCount(result.count)
      }
    } catch (err) {
      recordFailure(err instanceof Error ? err.message : err)
      reportClientError({
        errorId: ERROR_IDS.NOTIFICATION_STREAM_REFRESH_FAILED,
        action: 'getUnreadNotificationCount',
        error: err,
      })
    }
  }, [recordFailure])

  // Automatic reads (poll ticks, SSE-failure fallback, tab refocus) wait out
  // the backoff; a manual `refresh()` does not. Without this, every SSE
  // reconnect during an outage restarted polling with an immediate read.
  const pollRefresh = useCallback(async () => {
    if (pollInFlightRef.current) return
    if (Date.now() < nextPollAtRef.current) return
    pollInFlightRef.current = true
    try {
      await refresh()
    } finally {
      pollInFlightRef.current = false
    }
  }, [refresh])

  // Start polling fallback
  const startPolling = useCallback(() => {
    // Clear any existing interval
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current)
    }

    // Initial fetch
    pollRefresh()

    // Poll every 60 seconds
    pollIntervalRef.current = setInterval(pollRefresh, FALLBACK_POLL_INTERVAL)
  }, [pollRefresh])

  // Stop polling
  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current)
      pollIntervalRef.current = null
    }
  }, [])

  // Connect to SSE
  const connect = useCallback(() => {
    // Don't connect if already connected or unmounted
    if (eventSourceRef.current || !isMountedRef.current) {
      return
    }

    // Check if EventSource is available (not available in SSR)
    if (typeof window === 'undefined' || !window.EventSource) {
      startPolling()
      return
    }

    try {
      const eventSource = new EventSource(SSE_ENDPOINT)
      eventSourceRef.current = eventSource

      eventSource.onopen = () => {
        if (isMountedRef.current) {
          setIsConnected(true)
          setError(null)
          // The server is reachable again (#16518): clear the poll backoff so
          // a refocus read is not held for up to MAX_POLL_BACKOFF.
          consecutiveFailuresRef.current = 0
          nextPollAtRef.current = 0
          stopPolling() // Stop polling when SSE connects
        }
      }

      eventSource.onmessage = event => {
        if (isMountedRef.current) {
          try {
            const data: NotificationEvent = JSON.parse(event.data)
            setUnreadCount(data.unreadCount)
          } catch (err) {
            reportClientError({
              errorId: ERROR_IDS.NOTIFICATION_STREAM_PARSE_FAILED,
              action: 'parseSseMessage',
              error: err,
            })
          }
        }
      }

      eventSource.onerror = () => {
        // Close current connection
        eventSource.close()
        eventSourceRef.current = null

        if (isMountedRef.current) {
          setIsConnected(false)
          setError(new Error('SSE connection failed'))

          // Start polling as fallback
          startPolling()

          // Try to reconnect after delay
          reconnectTimeoutRef.current = setTimeout(() => {
            if (isMountedRef.current) {
              connect()
            }
          }, RECONNECT_DELAY)
        }
      }
    } catch (err) {
      if (isMountedRef.current) {
        setError(err instanceof Error ? err : new Error('Failed to connect'))
        startPolling()
      }
    }
  }, [startPolling, stopPolling])

  // Disconnect from SSE
  const disconnect = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close()
      eventSourceRef.current = null
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
      reconnectTimeoutRef.current = null
    }
    stopPolling()
    setIsConnected(false)
  }, [stopPolling])

  // Main effect: Connect on mount, cleanup on unmount
  // Issue #2135: Also handles user ID changes by detecting when userId differs from the connected user
  useEffect(() => {
    isMountedRef.current = true

    // Check if this is a user change (not initial mount)
    const isUserChange =
      connectedUserIdRef.current !== undefined &&
      connectedUserIdRef.current !== userId

    if (isUserChange) {
      // User changed - reset state for new user (Issue #2135)
      setUnreadCount(0)
      setError(null)
      consecutiveFailuresRef.current = 0
      nextPollAtRef.current = 0
    }

    // Track the user we're connecting for
    connectedUserIdRef.current = userId
    connect()

    return () => {
      isMountedRef.current = false
      disconnect()
    }
  }, [connect, disconnect, userId])

  // Visibility change handler: Reconnect when tab becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Refresh count immediately when tab becomes visible
        pollRefresh()

        // Reconnect if not connected
        if (!eventSourceRef.current) {
          connect()
        }
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [connect, pollRefresh])

  // Issue #2663: Listen for custom notifications-updated events
  // This allows server actions to trigger a refresh when SSE doesn't deliver
  // the event (e.g., in serverless environments where the in-memory EventEmitter
  // doesn't work across process boundaries)
  useEffect(() => {
    const handleNotificationsUpdated = () => {
      if (isMountedRef.current) {
        refresh()
      }
    }

    window.addEventListener(
      NOTIFICATIONS_UPDATED_EVENT,
      handleNotificationsUpdated,
    )

    return () => {
      window.removeEventListener(
        NOTIFICATIONS_UPDATED_EVENT,
        handleNotificationsUpdated,
      )
    }
  }, [refresh])

  return {
    unreadCount,
    isConnected,
    error,
    refresh,
  }
}
